/**
 * Clinical Supervisor — per-therapist simulation grants panel.
 *
 * Lists the therapist's service-group simulation library + methodology library.
 * Items in the group's defaults are pre-checked + locked (auto-granted to all
 * therapists). Items NOT in defaults can be granted individually.
 *
 * Also exposes the per-therapist override of AI feedback visibility (inherit /
 * auto / supervisor_gated / never) — useful when the group default is gated
 * but a particular therapist is comfortable with auto-feedback (or vice versa).
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Checkbox, FormControlLabel, Button, Alert,
  CircularProgress, Divider, Chip, Snackbar, Radio, RadioGroup, FormLabel
} from '@mui/material';
import api from '../../services/api';

export default function TherapistSimulationGrants({ therapistId, token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [toast, setToast]     = useState(null);
  const [data, setData]       = useState(null);
  const [grants, setGrants]   = useState([]);
  const [methGrants, setMethGrants] = useState([]);
  const [visibilityOverride, setVisibilityOverride] = useState('inherit');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/simulation-governance/supervisor/therapists/${therapistId}/simulation-grants`, token);
      const d = res?.data || null;
      setData(d);
      setGrants(d?.grants || []);
      setMethGrants(d?.methodologyGrants || []);
      setVisibilityOverride(d?.therapistVisibilityOverride || 'inherit');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load grants');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (therapistId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [therapistId, token]);

  function toggle(setFn, list, id) {
    setFn((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = {
        grants,
        methodologyGrants: methGrants,
        feedbackVisibilityOverride: visibilityOverride === 'inherit' ? null : visibilityOverride
      };
      await api.patch(`/simulation-governance/supervisor/therapists/${therapistId}/simulation-grants`, body, token);
      setToast('Simulation grants saved');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  const library      = (data.library || []).map(String);
  const defaults     = new Set((data.defaults || []).map(String));
  const methLibrary  = (data.methodologyLibrary || []).map(String);
  const methDefaults = new Set((data.methodologyDefaults || []).map(String));
  const grantedScenarios = new Set(grants.map(String));
  const grantedMeths     = new Set(methGrants.map(String));
  const groupVisibility  = data.groupVisibility || 'supervisor_gated';

  if (library.length === 0 && methLibrary.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        This therapist's service group has no simulation library configured yet.
        Ask the admin to set one on the Service Group page first.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Items in the service group <strong>defaults</strong> are auto-granted and locked. Tick additional
        scenarios + rubrics to grant beyond the group default.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip size="small" label={`Library: ${library.length}`} variant="outlined" />
        <Chip size="small" label={`Group default scenarios: ${defaults.size}`} color="success" variant="outlined" />
        <Chip size="small" label={`Therapist add-ons: ${grants.length}`} color="primary" variant="outlined" />
      </Stack>

      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          AI feedback visibility — per-therapist override
        </FormLabel>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Group default: <strong>{groupVisibility}</strong>
        </Typography>
        <RadioGroup value={visibilityOverride} onChange={(e) => setVisibilityOverride(e.target.value)}>
          <FormControlLabel value="inherit" control={<Radio size="small" />} label={`Inherit group default (${groupVisibility})`} />
          <FormControlLabel value="auto" control={<Radio size="small" />} label="Auto — therapist sees AI scores immediately" />
          <FormControlLabel value="supervisor_gated" control={<Radio size="small" />} label="Supervisor-gated — you release scores per session" />
          <FormControlLabel value="never" control={<Radio size="small" />} label="Never — AI scores are QA-only" />
        </RadioGroup>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Scenarios</Typography>
      <Stack divider={<Divider />} spacing={0} sx={{ mb: 3 }}>
        {(data.scenarios || []).map((s) => {
          const id = String(s._id);
          const isDefault = defaults.has(id);
          const isGranted = grantedScenarios.has(id);
          return (
            <Stack key={id} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}
              justifyContent="space-between" spacing={1} sx={{ py: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">{s.name}</Typography>
                  {isDefault && <Chip size="small" label="group default" color="success" variant="outlined" />}
                  {isGranted && !isDefault && <Chip size="small" label="granted" color="primary" variant="outlined" />}
                  {s.difficulty && <Chip size="small" label={s.difficulty} variant="outlined" />}
                </Stack>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={isDefault || isGranted} disabled={isDefault}
                    onChange={() => toggle(setGrants, grants, id)} />
                }
                label={isDefault ? 'Auto' : 'Grant'}
              />
            </Stack>
          );
        })}
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Evaluation rubrics</Typography>
      {(data.methodologies || []).length === 0 ? (
        <Typography variant="caption" color="text.secondary">No rubrics in this clinic's library.</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {(data.methodologies || []).map((m) => {
            const id = String(m._id);
            const isDefault = methDefaults.has(id);
            const isGranted = grantedMeths.has(id);
            return (
              <Stack key={id} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}
                justifyContent="space-between" spacing={1} sx={{ py: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{m.name}</Typography>
                    {isDefault && <Chip size="small" label="group default" color="success" variant="outlined" />}
                    {isGranted && !isDefault && <Chip size="small" label="granted" color="primary" variant="outlined" />}
                    {m.organisation && <Chip size="small" label={m.organisation} variant="outlined" />}
                  </Stack>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={isDefault || isGranted} disabled={isDefault}
                      onChange={() => toggle(setMethGrants, methGrants, id)} />
                  }
                  label={isDefault ? 'Auto' : 'Grant'}
                />
              </Stack>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={load} disabled={saving}>Reset</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save grants'}</Button>
      </Stack>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
